import type { TradeType } from './trades.types';

export type BillingAgreement = 'gmsla' | 'gmra';
export type BillingStatus = 'draft' | 'issued' | 'paid';
export type AccrualKind = 'fee' | 'rebate' | 'combined';

/**
 * Which KIND of document a statement row is (LR-558) — not to be confused with
 * `agreement` (gmsla/gmra), which is the master agreement behind a trade
 * statement. Mirae R11.4 names exactly these two: "GMSLA Billing" and "Cash Pool
 * Interest Statement (per currency per CP)".
 *
 * A `cash_pool_interest` row carries a BLANK `agreement` — the pool backs both
 * agreements, so the split does not apply to it — and its document has a
 * different shape (see `CashPoolDocument`).
 */
export type BillingStatementType = 'trade_billing' | 'cash_pool_interest';

// --- Manual adjustments (LR-563) ---------------------------------------------
// Direction of a manual billing adjustment. Stored as the SIGN of `amount`
// (positive = due to us), decomposed here into a toggle so the FE never reasons
// about the sign itself. The trade type is the trades' own `TradeType` — an
// adjustment corrects a trade, so the vocabularies must not drift (the backend
// reuses PositionDetails.TRADE_TYPE_CHOICES for the same reason).
export type AdjustmentDirection = 'to_us' | 'to_cp';

// Export formats for the statement download endpoint. 'csv' (LR-505) and 'pdf'
// (LR-506) are served; 'xlsx' stays in the union as the extension point the
// download hooks are parameterised on, but the backend 400s it - see
// DOWNLOAD_FORMATS in BillingDocumentPage for why.
export type StatementExportFormat = 'csv' | 'xlsx' | 'pdf';

// --- Background export (LR-728) -----------------------------------------------
// A download is built by a worker, not inside the request: the page starts a job
// (POST on the export URL), polls it and fetches the file once it is `done`.
export type BillingExportStatus = 'queued' | 'running' | 'done' | 'failed';

/**
 * How far a running export is. `building`: `done` of `total` document rows
 * written - `total` is known from the first report for a trade document, and
 * null for a cash pool one, which is built whole with nothing to count.
 * `rendering` (PDF only): the layout is one step, so `total` is null there too.
 */
export interface BillingExportProgress {
  phase: 'building' | 'rendering';
  done: number;
  total: number | null;
}

export interface BillingExportJob {
  id: number;
  status: BillingExportStatus;
  /** Null until the worker has priced the document, and once the job ended. */
  progress: BillingExportProgress | null;
  filename: string;
  /** Why a `failed` job failed, written for the user. */
  error: string;
  created_at: string;
  finished_at: string | null;
}

export interface AccrualEntry {
  id: number;
  position: number;
  position_symbol_id: string;
  billing_statement: number | null;
  accrual_date: string;
  accrual_kind: AccrualKind;
  accrual_kind_display: string;
  day_count_basis: number;
  rate_used: string | null;
  benchmark_rate_used: string | null;
  spread_or_fee_used: string | null;
  contract_value_used: string | null;
  last_marked_price_used: string | null;
  cost_of_collateral: string | null;
  fee_income: string | null;
  interest_amount: string | null;
  net_amount: string;
  // LR-749: the booked security→settlement rate the row's money was converted
  // at. Set: every money field above is in the SETTLEMENT currency; null: in the
  // security currency (same-currency trade, repo, or a row from before LR-749).
  fx_rate: string | null;
  is_weekend: boolean;
  is_holiday: boolean;
  holiday_country: string | null;
  notes: string;
  created_at: string;
}

export interface BillingStatementListItem {
  id: number;
  counterparty_name: string;
  counterparty_id: number;
  counterparty_account: string;
  period_start: string;
  period_end: string;
  currency: string;
  /** Blank string on a cash pool interest statement — see BillingStatementType. */
  agreement: BillingAgreement | '';
  agreement_display: string;
  /** LR-558. Always present; defaults to 'trade_billing' server-side. */
  statement_type: BillingStatementType;
  statement_type_display: string;
  total_accrued: string;
  status: BillingStatus;
  status_display: string;
  /** No app action sets it (mark-paid moves draft -> paid, LR-556), so the list
   * has no Issued column (LR-681); an admin edit can still fill it. */
  issued_at: string | null;
  /** Settled date (`YYYY-MM-DD`) recorded by mark-paid (LR-556). */
  paid_at: string | null;
  /**
   * Actual amount paid/received, Decimal string; same sign convention as
   * total_accrued (positive = due to us), so the discrepancy is a direct
   * subtraction (LR-556).
   */
  paid_amount: string | null;
  /** Who recorded the payment (LR-556; list-level since LR-557 — the document
   * page's paid strip is fed from list rows). */
  paid_by: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

// --- Month-to-date preview (LR-508 / T5) --------------------------------------
// Read-only view of the in-memory StatementView the backend builds on the fly
// (GET /billing/preview/, never persisted). Shapes mirror the backend
// StatementViewSerializer 1:1. Decimals arrive as strings (DRF
// COERCE_DECIMAL_TO_STRING) to preserve precision - format for display only,
// never do money math on them in the FE.

export interface PreviewRow {
  accrual_date: string;
  quantity: number | null;
  /** True when `quantity` was rebuilt from the live position (pre-LR-553 row). */
  quantity_is_estimated: boolean;
  /**
   * LR-632: the RAW per-unit price, so `price x quantity = gross_value` reads as
   * one calculation on every row - on a margined row that is the mark the base
   * was built from, not the margined price the row accrued on.
   */
  price: string | null;
  /** The base this line ACCRUED on: gross on a fee leg (`accrual_kind`
   * 'fee'), margined cash collateral everywhere else (LR-555). It is one of the
   * two figures below. */
  contract_value: string | null;
  /** LR-632: both sides of `gross x margin = cash collateral`. Absent on a
   * payload built before LR-632 (rolling deploy). */
  gross_value: string | null;
  cash_collateral: string | null;
  /**
   * Rebate-mode figures. `spread_or_fee` is null on every fee-mode row since
   * LR-632 (the fee % is `rate` there); `benchmark_rate` is null on a fee-mode
   * row that prints a split (the benchmark is `collateral_rate`), on a fee leg,
   * where it is inert, and on a fixed rebate booked as one rate, whose
   * benchmark is the rate (unless `rate_is_blended`).
   */
  benchmark_rate: string | null;
  spread_or_fee: string | null;
  /**
   * LR-618: the rate this line ACCRUED AT, carrying the rate's own sign and
   * never the direction of payment - that lives in `net_amount`'s sign alone.
   * Invariant on a single-flow line: `| |contract_value x rate| / 100 /
   * day_count_basis - |net_amount| | <= 0.01`, in magnitude because the two
   * signs are independent. A line that prints a split (LR-632) is two such
   * triplets instead: `gross_value x rate` = `fee_income`, `cash_collateral x
   * collateral_rate` = `cost_of_collateral`.
   */
  rate: string | null;
  /**
   * LR-618: true when `rate` is a netted blend rather than a contracted rate.
   * Since LR-632 a fee-mode day whose halves provably add up prints them and the
   * fee % instead, so the blend is left to lines that cannot prove a split and to
   * a changed basis. Surfaced once per position, not per line.
   */
  rate_is_blended: boolean;
  day_count_basis: number;
  net_amount: string;
  /**
   * LR-632: what `net_amount` is made of on a fee-mode day - the fee on
   * `gross_value` at `rate`, the cost on `cash_collateral` at `collateral_rate`.
   * Both are already signed the way `net_amount` is, so they ADD to it. Null
   * where the ledger holds no split (a rebate-mode or GMRA row is a single
   * flow) and on a fee-mode row whose halves do not add up to its `net_amount`
   * (a bare magnitude, whatever its date); absent on a payload built before
   * LR-632.
   */
  fee_income: string | null;
  collateral_rate: string | null;
  cost_of_collateral: string | null;
  /**
   * LR-619 (feedback #7): `net_amount` at cent precision, allocated across the
   * block so the printed column sums to the printed footing. Render THIS, not a
   * client-rounded `net_amount` — the FE rounds half-up and the allocator
   * half-even, so re-rounding an exact half-cent would show two different
   * subtotals in one block. `net_amount` stays 8dp beside it and is what the
   * drift check compares. Null on a payload built before LR-619.
   */
  amount: string | null;
  /**
   * LR-554: the day's events (the trade's first line `Open` and a re-mark `MTM`
   * since LR-632, a settled partial recall, an applied re-rate, maturity /
   * termination) joined with '; ', or null on an ordinary day. Rendered as the
   * Action column; the CSV/PDF export carries the same text. Always present in the
   * response - nullable, not optional.
   */
  action: string | null;
}

/**
 * LR-632: the trade's reference columns, constant across the group's lines -
 * what the security is, in which currencies, on which side and against what
 * collateral. Carried on the GROUP rather than on every row because that is what
 * they describe, and because the footed document has no rows at all (LR-571).
 */
export interface PositionRef {
  description: string;
  sedol: string;
  trade_date: string | null;
  /** 'L' for the lend side (lend / repo), 'B' for the borrow one. */
  side: string;
  /** CASH / NON-CASH / UNCOLLATERALIZED, or CASH POOL when the cash collateral
   * sits in a pool (LR-586): what secures the trade is then a pooled balance. */
  collateral: string;
  /** The margin RATIO ('1.0250' for 102.5 %), not basis points. */
  margin: string | null;
  /** The security's own currency. `settlement_currency` is the block the trade
   * is filed under (a blank one on a legacy row comes back as `currency`); they
   * differ on a cross-currency trade, which `is_cross_currency` flags. Its lines
   * are in the settlement currency once converted (LR-749, the accrual's
   * `fx_rate`). */
  currency: string;
  settlement_currency: string;
  is_cross_currency: boolean;
  /** The counterparty's account the trade is booked to: a block is one
   * currency whatever accounts its trades sit in (Tim, 2026-10-30). */
  account: string;
}

/**
 * One position's block within a section: its footing, and how to get its days.
 *
 * `rows` is EMPTY on the document endpoints (LR-571). A counterparty's month can
 * run to hundreds of thousands of daily lines - measured 746k lines -> a 206 MB
 * response and a 502 - so the document is footed in the database and the lines
 * are fetched one position at a time (`getPreviewRows` / `getStatementRows`).
 * `row_count` and the date range describe them without loading them, which is
 * what the UI needs to decide whether a group is worth expanding.
 */
export interface PreviewPositionGroup {
  position_id: number;
  symbol_id: string;
  isin: string;
  trade_type: string;
  ref: PositionRef;
  subtotal: string;
  /** LR-632: `subtotal` at the cent the document prints it at, allocated like
   * the file's. Null outside a document; absent on a pre-LR-632 payload. */
  amount: string | null;
  /** Daily lines behind `subtotal`, whether or not `rows` carries them. */
  row_count: number;
  first_date: string;
  last_date: string;
  rows: PreviewRow[];
}

/** One position's daily lines within one section, plus that block's footing. */
export interface PositionSectionRows {
  key: StatementSectionKey;
  title: string;
  subtotal: string;
  /**
   * LR-619: `subtotal` at cent precision, and the figure the rows' `amount`
   * cells were allocated against — so the printed column adds up to it exactly.
   * `subtotal` stays the full-precision one everything reconciles on. Null on a
   * payload built before LR-619 (rolling deploy).
   */
  amount_total: string | null;
  row_count: number;
  rows: PreviewRow[];
}

/**
 * The expanded ledger of one position (LR-571). Sectioned rather than flat: a
 * fee-mode FOP trade books a fee leg and a rebate leg on the same day, so its
 * lines belong under two different headings.
 */
export interface PositionRows {
  position_id: number;
  symbol_id: string;
  isin: string;
  trade_type: string;
  sections: PositionSectionRows[];
}

/**
 * One economic bucket of the statement: fees on top, the interest on the
 * collateral behind them below, each with its own subtotal. Switch on `key`
 * (`fees`, `cash_rebate`, `cash_pool`, `repo_interest`); `title` / `subtitle`
 * are display strings and may be retuned. A section can be present and empty -
 * `cash_pool` is a structural placeholder.
 *
 * LR-632 merged `stock_lend_fee` and `stock_borrow_fee` into `fees`: the split
 * claimed one side was income and the other cost, which its own rows contradict
 * (a borrow above par margin nets to income). The side is on the trade now.
 */
// Mirrors SECTION_ORDER in backend/src/apps/billing/services/statement_builder.py.
// The wire field is a plain CharField, so this union is a convention, not a
// guarantee: a section added server-side without updating it here narrows
// silently. Consumers keep a runtime fallback (see SECTION_SHORT_LABELS).
export type StatementSectionKey =
  'fees' | 'cash_rebate' | 'cash_pool' | 'repo_interest';

/** LR-632: one security's footing inside a section, present only where that
 * security carries more than one trade - a total over a single line repeats it. */
export interface SecurityTotal {
  symbol_id: string;
  subtotal: string;
  trade_count: number;
  /** The sum of the trades' allocated `amount`s. Null outside a document;
   * absent on a pre-LR-632 payload. */
  amount: string | null;
}

export interface PreviewSection {
  key: StatementSectionKey;
  title: string;
  subtitle: string;
  subtotal: string;
  row_count: number;
  is_empty: boolean;
  groups: PreviewPositionGroup[];
  /** Absent on a payload built before LR-632 (rolling deploy). */
  securities: SecurityTotal[];
  /** LR-632: `subtotal` at the cent the document prints it at - the summary's
   * section net. Null outside a document and on an empty section. */
  amount: string | null;
}

export interface PreviewHeader {
  statement_id: number | null;
  counterparty_id: number;
  counterparty_name: string;
  counterparty_account: string;
  period_start: string;
  period_end: string;
  currency: string;
  agreement: BillingAgreement;
  status: string;
  total_accrued: string;
}

export interface StatementView {
  header: PreviewHeader;
  sections: PreviewSection[];
  grand_total: string;
}

// --- Document (LR-553 item 3) -------------------------------------------------
// One document per counterparty per period; currencies are its sections. Only
// the presentation changed - BillingStatement is still one row per (counterparty,
// period, currency, agreement), because payment and status genuinely are per
// currency (transfers go currency by currency).
//
// Note what is absent: there is NO total across currencies. Product refused one -
// figures in different denominations are not addable - so the header carries a
// per-currency summary and each block closes on its own total.

/**
 * LR-632: one section of one currency in the opening summary. `due_to_us` and
 * `due_to_them` are the two halves of `net`, split by the sign of each TRADE's
 * footing - the reference layout's Payable / Receivable pair, in the ledger's
 * own signs, which is also how the document prints them, under the label each
 * half earns from Exante's side (like the P/R letter): `due_to_them` is the
 * Payable half, `due_to_us` the Receivable one.
 */
export interface CurrencySectionTotal {
  key: StatementSectionKey;
  title: string;
  due_to_us: string;
  due_to_them: string;
  net: string;
}

export interface CurrencyTotal {
  currency: string;
  total: string;
  /** Absent on a payload built before LR-632 (rolling deploy). */
  sections: CurrencySectionTotal[];
}

export interface CurrencyBlock {
  currency: string;
  agreement: BillingAgreement;
  /** Null on a month-to-date preview: nothing is persisted yet. */
  statement_id: number | null;
  status: string;
  /** This block's own statement total, penny-exact against its database row. */
  total: string;
  sections: PreviewSection[];
}

export interface StatementDocumentHeader {
  counterparty_id: number | null;
  counterparty_name: string;
  period_start: string | null;
  period_end: string | null;
  currency_totals: CurrencyTotal[];
}

export interface StatementDocument {
  header: StatementDocumentHeader;
  blocks: CurrencyBlock[];
}

// --- Cash Pool Interest Statement (LR-558) ------------------------------------
// A different document with a different column set (feedback #74: Date,
// Collateral Value (with margin), Rate, Number of Days, Interest Amount,
// benchmark, spread, Currency), served by the SAME
// `/billing/statements/{id}/document/` endpoint. ONE document per counterparty
// with every pool as a section of it (product answer #15, 2026-08-04), nested
// currency block -> pool section -> daily lines. The currency block exists
// because a currency is what settles: it carries its own total, its own paid
// status and its own transfer.

export interface CashPoolRow {
  /** First calendar day the line covers. */
  start_date: string;
  /** Last day it covers - equal to `start_date` on a one-day line. */
  end_date: string;
  /**
   * Collateral value WITH margin, in the statement's own sign convention:
   * NEGATIVE when we hold the counterparty's cash and therefore owe interest.
   */
  collateral_value: string;
  benchmark: string;
  benchmark_rate: string | null;
  /**
   * Null on a fixed pool (no benchmark to spread over) and on a day accrued
   * before LR-587, when the repo pool had no rate definition to read.
   */
  spread: string | null;
  rate: string;
  /**
   * Calendar days this line covers. The pool balance is a step function - it
   * only moves when a margin call is posted - so a line legitimately spans a
   * weekend at one balance and one (Friday) fixing.
   */
  days: number;
  day_count_basis: number;
  interest_amount: string;
}

/** One pool's lines within a currency block. */
export interface CashPoolSection {
  /** Null for the implicit repo pool, which has no entity behind it (LR-587). */
  pool_id: number | null;
  /** The pool's own name plus its product, e.g. `Mirae USD SOFR+25 (GMRA)`. */
  label: string;
  product: string;
  total: string;
  /**
   * The last day this pool accrued within the document (LR-654) - the
   * freshness mark the MTD preview prints under the pool heading. Per pool, not
   * per document: one currency block can hold several pools, and a pool whose
   * fixing stalled must not read as current beside one that ran on. Sent on
   * every document; only the preview renders it. Optional: a not-yet-rolled
   * backend omits the key.
   */
  data_through?: string;
  rows: CashPoolRow[];
}

/** Everything the counterparty settles in one currency: one persisted statement. */
export interface CashPoolBlock {
  currency: string;
  statement_id: number | null;
  /**
   * The statement's own status — the list row's enum, plus `'preview'` on the
   * unpersisted month-to-date document (LR-624), matching the trade preview's
   * blocks.
   */
  status: BillingStatus | 'preview';
  /** The persisted statement total, penny-exact against its database row. */
  total: string;
  /** Live sum of the lines - equal to `total` unless the statement has drifted. */
  recomputed_total: string;
  sections: CashPoolSection[];
}

/**
 * One repo margin call posting within the document's period (LR-624). Read
 * verbatim off the RepoMarginCall row; all three money figures are denominated
 * in `currency` — the call's settlement currency. Sign is the shared canon:
 * positive = we hold the counterparty's cash.
 *
 * BOOK, not pool: `book_before` / `book_after` are one (counterparty,
 * trade-currency) netting book's own collateral at posting time, which is NOT
 * the pool balance the interest lines accrue on — several books may share a
 * pool, and a GMSLA pool takes no margin call at all. `book` is that book's
 * trade currency, so the running balance reads down within one book.
 */
export interface CashPoolPosting {
  posted_on: string;
  book: string;
  amount: string;
  currency: string;
  book_before: string;
  book_after: string;
}

export interface CashPoolDocument {
  counterparty_id: number;
  counterparty_name: string;
  period_start: string;
  period_end: string;
  /** No grand total across blocks: two currencies do not add up. */
  blocks: CashPoolBlock[];
  /**
   * The period's repo margin call postings, one flat document-level section
   * with no total of its own (LR-624). Optional: a document served by a
   * not-yet-rolled backend omits the key — render as an absent section, not a
   * crash.
   */
  postings?: CashPoolPosting[];
}

/**
 * What `/billing/statements/{id}/document/` returns: the shape depends on the
 * statement's type. Discriminate with `isCashPoolDocument` rather than on
 * `statement_type`, so a consumer holding only the document (the expandable row,
 * the document page) does not need the list row to know what it has.
 */
export type BillingDocument = StatementDocument | CashPoolDocument;

export const isCashPoolDocument = (
  document: BillingDocument | null | undefined
): document is CashPoolDocument =>
  // Null-safe: `'counterparty_id' in null` throws, and a JSON `null` body
  // reaches callers as data.
  //
  // Both shapes now carry `blocks`, so the discriminator is where the
  // counterparty lives: the pool document names it at the top level, the trade
  // document keeps it inside `header` with the period and the currency totals.
  // Asked POSITIVELY (has the pool key) rather than as "is not the trade shape":
  // a negative test answers `true` for anything unrecognised - an error envelope,
  // a partial body from a not-yet-rolled pod, a third document shape added later
  // - and the caller then reads `.blocks[].sections[]` off it.
  typeof document === 'object' &&
  document !== null &&
  'counterparty_id' in document;

/**
 * Response of the manual generate endpoint (LR-510). `statements` is the
 * counterparty+month statement list after generation (created, refreshed or
 * already-existing). `unbilled_remaining` is true when billable accruals were
 * left unbilled - their statement is no longer a draft (paid, or issued by hand
 * in the admin), or they were withheld as `magnitude_defects` below - so the
 * modal warns instead of closing as a silent success. `deferred_repo_trades`
 * counts the trades whose accruals were deliberately skipped as non-billable
 * repo (LR-552), so an empty result can say WHY nothing was generated instead of
 * just being empty. `magnitude_defects` (LR-627) counts rows withheld because
 * their money is a clean power of ten off their own rate; the modal names them
 * ahead of the finalized case because only they have a fix to ask for (a data
 * repair before any run can bill them).
 */
export interface GenerateStatementResult {
  statements: BillingStatementListItem[];
  unbilled_remaining: boolean;
  deferred_repo_trades: number;
  magnitude_defects: number;
}

/**
 * One (instrument, trade type) pair the counterparty has trades on — the unit an
 * adjustment is booked by, and one row of the instrument dropdown (LR-563).
 *
 * The collapse to pairs is done by the server, so these numbers describe the
 * whole matching book rather than a loaded window: `trade_count` is final and
 * `total_quantity` is the size across the pair.
 */
export interface AdjustmentTradeOption {
  symbol_id: string;
  trade_type: TradeType;
  trade_type_display: string;
  currency: string;
  description: string;
  trade_count: number;
  /** ISO timestamp of the most recent trade on the pair. */
  last_trade_date: string;
  total_quantity: number;
}

/**
 * One page of the pair picker. Not `PaginatedResponse`: this endpoint answers
 * `{count, results}` only — `count` is the number of PAIRS in the whole book, so
 * the caller pages by offset against it rather than following a `next` link.
 */
export interface AdjustmentTradeOptionPage {
  count: number;
  results: AdjustmentTradeOption[];
}

/**
 * A manual billing adjustment (LR-563): a one-line, out-of-band correction booked
 * by instrument + trade type as a single signed amount, posted to the Back Office
 * on a chosen Value Date. It never touches a BillingStatement. `amount` is the
 * signed Decimal string (positive = due to us — the total_accrued convention);
 * `abs_amount` + `direction` decompose it for the UI toggle. Editable/deletable
 * only while `is_editable` (unposted); after posting, a correction is a
 * counter-adjustment (`reverses`).
 */
export interface ManualAdjustment {
  id: number;
  counterparty: number;
  counterparty_name: string;
  symbol_id: string;
  trade_type: TradeType;
  trade_type_display: string;
  amount: string;
  abs_amount: string;
  direction: AdjustmentDirection;
  currency: string;
  value_date: string;
  comment: string;
  /** The posted adjustment this one reverses (сторно), or null. */
  reverses: number | null;
  /** ISO timestamp once posted to the Back Office; null while unposted. */
  posted_at: string | null;
  bo_external_id: string;
  is_posted: boolean;
  is_editable: boolean;
  /**
   * Whether a сторно already exists for this adjustment (server-side, so it does
   * not depend on which page of rows the client loaded). At most one is allowed.
   */
  is_reversed: boolean;
  /**
   * Whether posting to the Back Office is possible at all. False for every row
   * until BO-8040 lands the transaction type — the UI says so once instead of
   * letting each attempt fail with a 503.
   */
  can_post: boolean;
  created_by: string;
  /** Who authorised the Back Office posting; empty while unposted. */
  posted_by: string;
  created_at: string;
  updated_at: string;
}
